import * as React from "react";
import { UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { viewContext } from "../CheckPrices";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkText, UitkHeading, UitkSubheading } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";
import { useLocalization } from "@shared-ui/localization-context";
import IsomorphicCarousel from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { HotwireContainer } from "./views/HotwireContainer";

export interface ContainerProps {
  id: string;
  fmId: number;
  fmTitleId: number | null;
  title: string;
  view?: string;
  subHeadline: string;
  dataType: string;
}

export const Container: React.FC<ContainerProps> = (props) => {
  const view = React.useContext(viewContext);

  switch (view) {
    case "horizontal-data-display":
      return <RowViewContainer {...props} />;
    case "horizontal-card-carousel":
      return <CarouselViewContainer {...props} />;
    case "hotwire":
      return <HotwireContainer {...props} />;
    default:
      return <DefaultContainer {...props} />;
  }
};

const DefaultContainer: React.FC<Omit<ContainerProps, "view">> = ({
  children,
  fmId,
  fmTitleId,
  id,
  title,
  subHeadline,
}) => (
  <UitkSpacing padding={{ block: "three", inline: "three" }}>
    <div className="CheckPrices" id={id} data-fm={fmId} data-fm-title-id={fmTitleId}>
      <UitkCard padded>
        <UitkLayoutFlex space="three" wrap="wrap">
          <UitkLayoutFlexItem minWidth="full_width">
            <UitkHeading tag="h2" size={4}>
              {title}
            </UitkHeading>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem minWidth="full_width">
            {subHeadline && (
              <UitkText size={300} weight="regular" spacing="one">
                {subHeadline}
              </UitkText>
            )}
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem minWidth="full_width">
            <UitkCardContentSection padded={false}>{children}</UitkCardContentSection>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkCard>
    </div>
  </UitkSpacing>
);

const RowViewContainer: React.FC<Omit<ContainerProps, "view">> = ({
  children,
  fmId,
  fmTitleId,
  id,
  title,
  subHeadline,
}) => (
  <UitkSpacing padding={{ block: "three", inline: "four" }} margin="three">
    <div className="CheckPrices" id={id} data-fm={fmId} data-fm-title-id={fmTitleId}>
      {title && (
        <UitkCard padded>
          <UitkSpacing padding={{ blockend: "three" }}>
            <UitkHeading tag="h2" size={5}>
              {title}
            </UitkHeading>
          </UitkSpacing>
          {subHeadline && (
            <UitkSpacing margin={{ blockstart: "one" }}>
              <UitkSubheading tag="p"> {subHeadline} </UitkSubheading>
              <UitkText size={300} weight="regular">
                {subHeadline}
              </UitkText>
            </UitkSpacing>
          )}
          {title ? (
            <UitkSpacing padding={{ blockstart: "two" }}>
              <UitkLayoutFlex space="six" wrap="wrap">
                {children}
              </UitkLayoutFlex>
            </UitkSpacing>
          ) : (
            <UitkLayoutFlex space="six" wrap="wrap">
              {children}
            </UitkLayoutFlex>
          )}
        </UitkCard>
      )}
    </div>
  </UitkSpacing>
);

const CarouselViewContainer: React.FC<Omit<ContainerProps, "view">> = ({
  children,
  fmId,
  fmTitleId,
  id,
  title,
  subHeadline,
}) => {
  const { formatText } = useLocalization();

  return (
    <section
      className="CheckPrices all-y-padding-three all-x-padding-three"
      id={id}
      data-fm={fmId}
      data-fm-title-id={fmTitleId}
    >
      <h2 className="uitk-type-heading-500">{title}</h2>
      {subHeadline && <span className="uitk-type-300 uitk-type-regular all-t-margin-one">{subHeadline}</span>}
      <IsomorphicCarousel
        className="CarouselView all-t-margin-three"
        carouselName="DestinationCards"
        buttonText={{
          nextButton: formatText("carousel.item.next"),
          prevButton: formatText("carousel.item.prev"),
        }}
        itemsVisible={{ sm: 2, md: 3, lg: 3 }}
        pageBy={1}
        peekWidth={12}
      >
        {children as React.ReactChild[]}
      </IsomorphicCarousel>
    </section>
  );
};
